// ** MUI Theme Provider
import { deepmerge } from '@mui/utils';
import { createTheme, PaletteMode, ThemeOptions } from '@mui/material';

// ** User Theme Options
import UserThemeOptions from 'src/layouts/UserThemeOptions';

// ** Theme Override Imports
import palette from './palette';
import spacing from './spacing';
import shadows from './shadows';
import overrides from './overrides';
import typography from './typography';
import breakpoints from './breakpoints';

// Custom Colors
const customColors = {
    primary: {
        main: '#3E7BFA',
        dark: '#3568D4',
        light: '#6698FF',
    },
    dark: {
        900: '#1C1C28',
        800: '#28293D',
        700: '#555770',
        600: '#8F90A6',
        500: '#C7C9D9',
    },
    light: {
        900: '#E4E4EB',
        800: '#EBEBF0',
        700: '#F2F2F5',
        600: '#FAFAFC',
        500: '#FFFFFF',
    },
    red: {
        main: '#F44336',
        900: '#B71C1C',
        800: '#C62828',
        700: '#D32F2F',
        600: '#E53935',
        500: '#F44336',
    },
    green: {
        main: '#4CAF50',
        900: '#1B5E20',
        800: '#2E7D32',
        700: '#388E3C',
        600: '#43A047',
        500: '#4CAF50',
    },
    blue: {
        main: '#2196F3',
        900: '#0D47A1',
        800: '#1565C0',
        700: '#1976D2',
        600: '#1E88E5',
        500: '#2196F3',
    },
    yellow: {
        main: '#FFEB3B',
        900: '#F57F17',
        800: '#F9A825',
        700: '#FBC02D',
        600: '#FDD835',
        500: '#FFEB3B',
    },
    orange: {
        main: '#FF9800',
        900: '#E65100',
        800: '#EF6C00',
        700: '#F57C00',
        600: '#FB8C00',
        500: '#FF9800',
    },
    teal: {
        main: '#009688',
        900: '#004D40',
        800: '#00695C',
        700: '#00796B',
        600: '#00897B',
        500: '#009688',
    },
    purple: {
        main: '#9C27B0',
        900: '#4A148C',
        800: '#6A1B9A',
        700: '#7B1FA2',
        600: '#8E24AA',
        500: '#9C27B0',
    },
    gradients: {
        gradientBlue: 'linear-gradient(45deg, #3E7BFA, #3568D4)',
        gradientOrange: 'linear-gradient(45deg, #FF9800, #F57C00)',
        gradientPurple: 'linear-gradient(45deg, #9C27B0, #6A1B9A)',
        gradientGreen: 'linear-gradient(45deg, #4CAF50, #2E7D32)',
        gradientYellow: 'linear-gradient(45deg, #FFEB3B, #F9A825)',
        gradientTeal: 'linear-gradient(45deg, #009688, #00796B)',
    },
};

// Utility function to get contrasting color based on the current mode
const getContrastingColor = (mode, shade) => {
    return mode === 'dark' ? customColors.light[shade] : customColors.dark[shade];
};

// Custom Typography with Poppins font
const customTypography = {
    fontFamily: 'Poppins, Arial, sans-serif',
    h1: { fontWeight: 700, fontSize: '2.5rem', lineHeight: 1.2 },
    h2: { fontWeight: 600, fontSize: '2rem', lineHeight: 1.3 },
    h3: { fontWeight: 500, fontSize: '1.75rem', lineHeight: 1.4 },
    h4: { fontWeight: 500, fontSize: '1.5rem', lineHeight: 1.5 },
    h5: { fontWeight: 400, fontSize: '1.25rem', lineHeight: 1.6 },
    h6: { fontWeight: 400, fontSize: '1rem', lineHeight: 1.7 },
    body1: { fontWeight: 400, fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.43 },
};

// Custom theme configuration function
const themeOptions = (settings, overrideMode) => {
    const { skin, mode, direction, themeColor } = settings;

    // Generate initial theme configuration
    const userThemeConfig = UserThemeOptions();
    const mergedThemeConfig = deepmerge(
        {
            breakpoints: breakpoints(),
            direction,
            components: overrides(settings),
            palette: palette(mode === 'semi-dark' ? overrideMode : mode, skin),
            ...spacing,
            shape: { borderRadius: 10 },
            mixins: {
                toolbar: {
                    minHeight: 64,
                },
            },
            shadows: shadows(mode === 'semi-dark' ? overrideMode : mode),
            typography: customTypography,  // Use custom typography with Poppins
        },
        userThemeConfig
    );

    // Add dynamic color switching support
    const dynamicPalette = {
        mode,
        background: {
            default: mode === 'dark' ? customColors.dark[900] : customColors.light[500],
            paper: mode === 'dark' ? customColors.dark[800] : customColors.light[600],
        },
        text: {
            primary: getContrastingColor(mode, 800),
            secondary: getContrastingColor(mode, 700),
            disabled: getContrastingColor(mode, 600),
        },
        divider: getContrastingColor(mode, 500),
        ...customColors,
    };

    // Merge with dynamic colors and typography
    return deepmerge(mergedThemeConfig, {
        palette: dynamicPalette,
        typography: customTypography,
    });
};

// Exporting the theme function that uses createTheme for MUI ThemeProvider
const createCustomTheme = (settings, mode) => createTheme(themeOptions(settings, mode));

export default createCustomTheme;
